import { useState, useEffect } from 'react';
import dayjs from 'dayjs';

const useFetchWikiData = (selectedArticles, startDate, endDate, frequency) => {
  const [data, setData] = useState([]);

  useEffect(() => {
    if (selectedArticles.length === 0) {
      setData([]);
      return;
    }
    fetchData();
  }, [selectedArticles, startDate, endDate, frequency]);

  const fetchData = async () => {
    if (selectedArticles.length === 0) return;
  
    let startStr = startDate.format('YYYYMMDD');
    let endStr = endDate.format('YYYYMMDD');
  
    // Generate a complete list of dates
    let allDates = [];
    let current = dayjs(startDate);
    while (current.isBefore(endDate) || current.isSame(endDate)) {
      allDates.push(current.format('YYYYMMDD')); // YYYYMMDD format
      current = current.add(1, 'day');
    }
  
    const allSeries = await Promise.all(
      selectedArticles.map(async (article) => {
        const url = `https://wikimedia.org/api/rest_v1/metrics/pageviews/per-article/en.wikipedia.org/all-access/all-agents/${article}/daily/${startStr}/${endStr}`;
        const response = await fetch(url);
        const data = await response.json();
  
        let transformedData = allDates.map((date) => ({
          x: date, // Keep original YYYYMMDD format
          y: 0, // Default to 0
        }));
  
        if (data.items) {
          data.items.forEach((item) => {
            let date = item.timestamp.slice(0, 8);
            let index = transformedData.findIndex((entry) => entry.x === date);
            if (index !== -1) {
              transformedData[index].y = item.views; // Replace default 0 with actual data
            }
          });
        }
  
        // Convert to weekly, monthly, or yearly if needed
        if (frequency === 'weekly') {
          transformedData = convertToWeekly(transformedData);
        } else if (frequency === 'monthly') {
          transformedData = convertToMonthly(transformedData);
        } else if (frequency === 'yearly') {
          transformedData = convertToYearly(transformedData);
        }
  
        return {
          id: article,
          label: article.replace('_', ' '),
          data: transformedData,
        };
      })
    );
  
    setData(allSeries);
  };
  
  const convertToWeekly = (dailyData) => {
    if (dailyData.length === 0) return [];

    let weeklyData = [];
    let tempWeek = { x: dailyData[0].x }; // First week start
    let sum = 0;
    let count = 0;

    dailyData.forEach((entry, index) => {
      sum += entry.y;
      count++;

      if (count === 7 || index === dailyData.length - 1) {
        tempWeek.y = sum;
        weeklyData.push(tempWeek);
        sum = 0;
        count = 0;
        tempWeek = { x: entry.x };
      }
    });

    return weeklyData;
  };

  const convertToMonthly = (dailyData) => {
    if (dailyData.length === 0) return [];

    let monthlyData = {};
    
    dailyData.forEach((entry) => {
      let month = entry.x.slice(0, 6); // YYYYMM format
      if (!monthlyData[month]) {
        monthlyData[month] = { x: month, y: 0 };
      }
      monthlyData[month].y += entry.y;
    });

    return Object.values(monthlyData);
  };

  const convertToYearly = (dailyData) => {
    if (dailyData.length === 0) return [];

    let yearlyData = {};

    dailyData.forEach((entry) => {
      let year = entry.x.slice(0, 4); // YYYY format
      if (!yearlyData[year]) {
        yearlyData[year] = { x: year, y: 0 };
      }
      yearlyData[year].y += entry.y;
    });

    return Object.values(yearlyData);
  };

  return { data };
};

export default useFetchWikiData;
